import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import moment from "moment";
import { Button as BtnPrimary } from "@talentbait/shared-components";

import logger from "../../utils/LoggerFrontend";
import Actions from "../../Actions";
import { numberFormat } from "../../utils/Helpers";

const DEFAULT_EXTENSION_CONFIGURED_STATUS = "SCHEDULED";
const DEFAULT_EXTENSION_DURATION = 14;

// Styled components
const MainWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const Button = styled.div`
  background: #1e22ff;
  display: inline-block;
  padding: 10px 25px;
  margin-bottom: 20px;
  text-align: center;
  border-radius: 20px;
  font-size: 16px;
  cursor: pointer;
  color: white;
  position: relative;
  display: inline-block;
  margin: 0px;
  margin-right: 10px;
  text-align: center;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.1s ease-out;
  user-select: none;
  box-shadow: 0px 4px 4px 3px #0175a12e;
  &:hover {
    transform: translateY(-3px) scale(1.005) translateZ(0);
  }
`;
const ModalContentWrapper = styled.div`
  padding: 30px 30px 20px;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
`;
const Description = styled.span`
  font-size: 16px;
`;
const Card = styled.div`
  display: flex;
  position: relative;
  border: 2px solid #e3e3e3;
  box-shadow: 0px 4px 5px 1px #a7a7a72e;
  height: 200px;
  border-radius: 10px;
  margin: 20px;
  justify-content: center;
`;
const ChannelsSection = styled.div`
  margin: 20px 20px;
  text-align: left;
  img {
    width: 25%;
    margin-right: 5px;
  }
`;
const BenefitSection = styled.div`
  margin: 20px 20px;
  text-align: left;
`;
const SectionTitle = styled.span`
  display: block;
  font-size: 16px;
  color: grey;
  margin-bottom: 20px;
`;
const SectionText = styled.span`
  display: block;
`;
const PriceSection = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
`;
const ChannelLogo = styled.img`
  margin: 2px 4px;
`;
const ActionButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 20px;
`;

const calculateBudget = ({ lifetimeBudget, duration, newDuration = 15 }) => {
  const price = parseInt((lifetimeBudget / duration) * newDuration);
  return { newLifetimeBudget: price, newMediaBudget: parseInt(price / 2) };
};

const ModalContent = ({
  remainingDays,
  channels = [],
  price,
  totalImpressions = 20000,
  isLeadCampaign = false,
  campaignId,
  newLifetimeBudget,
  newMediaBudget,
  locations,
  campaignName
}) => {
  const [isExtendingCampaign, setIsExtendingCampaign] = useState(false);
  let timer = useRef(null);
  useEffect(() => {
    return () => {
      const copyTimer = timer;
      if (copyTimer.current) {
        clearTimeout(copyTimer.current);
      }
    };
  }, [timer]);

  const handleExtendCampaign = useCallback(() => {
    setIsExtendingCampaign(true);
    const startDate = moment().add(remainingDays, "days").format();
    const endDate = moment()
      .add(remainingDays + DEFAULT_EXTENSION_DURATION, "days")
      .format();

    const adsetData = {
      budget: newLifetimeBudget,
      mediaBudget: newMediaBudget,
      locations,
      configuredStatus: DEFAULT_EXTENSION_CONFIGURED_STATUS,
      startDate,
      endDate,
      duration: DEFAULT_EXTENSION_DURATION,
      channels,
      name: "Verlängerung: " + campaignName
    };
    logger.log("ExtensionButton:extendCampaign", "prepared adset data", { adsetData });

    const ref = Actions.extendCampaign.completed.listen((payload) => {
      logger.log("ExtensionButton:extendCampaign", "completed", { payload });
      Actions.addNotification("Verlängerung erfolgreich");
      Actions.getAdsetsByCampaignId(campaignId);
      setIsExtendingCampaign(false);
      Actions.toggleModal("showGenericModal", false);
      ref();
    });
    const refFailed = Actions.extendCampaign.failed.listen((error) => {
      logger.log("error:ExtensionButton:extendCampaign", "failed", { error });
      Actions.addNotification("Fehler beim Verlängerung", "error");
      setIsExtendingCampaign(false);
      Actions.toggleModal("showGenericModal", false);
      refFailed();
    });
    timer.current = setTimeout(() => {
      Actions.extendCampaign(campaignId, adsetData);
    }, 2000);
  }, [
    remainingDays,
    locations,
    campaignId,
    channels,
    newLifetimeBudget,
    newMediaBudget,
    campaignName
  ]);

  logger.log("ExtensionButton:ModalContent", "render", { isExtendingCampaign });
  let DescriptionComponent = (
    <Description>
      Diese Kampagne endet in <strong>{remainingDays}</strong> Tage.
      <br />
      <br />
      Wollen sie den für <strong>14</strong> mehr Tagen nach dem Ende verlängern?
    </Description>
  );
  if (remainingDays <= 0) {
    DescriptionComponent = (
      <Description>
        Diese Kampagne ist schon beendet.
        <br />
        <br />
        Wollen sie den für <strong>14</strong> mehr Tagen verlängern?
      </Description>
    );
  }
  return (
    <ModalContentWrapper>
      {isExtendingCampaign ? (
        <center
          className="blinking"
          style={{
            fontSize: "20px",
            width: "100%",
            opacity: 0.5
          }}
        >
          Wir verlängern die Kampagne
          <br />
          auf den gewählten Kanälen…
        </center>
      ) : (
        <>
          {DescriptionComponent}
          <Card>
            <ChannelsSection>
              <SectionTitle>Kanäle</SectionTitle>
              {channels.map((channel, index) => (
                <ChannelLogo src={`/images/${channel}.png`} key={index} />
              ))}
            </ChannelsSection>
            <BenefitSection>
              <SectionTitle>mehre Rechweite</SectionTitle>
              <SectionText>{numberFormat(totalImpressions)} Impressionen</SectionText>
            </BenefitSection>
            {isLeadCampaign ? (
              <BenefitSection>
                <SectionTitle>Bewerber</SectionTitle>
                <SectionText>≈ 10 mehr Bewerber</SectionText>
              </BenefitSection>
            ) : (
              <></>
            )}
            <PriceSection>einmalig {price}€ / 14 Tage</PriceSection>
          </Card>
          <ActionButtonWrapper>
            <BtnPrimary
              type="tertiary"
              variant="outlined"
              onClick={() => Actions.toggleModal("showGenericModal", false)}
              mr={"5px"}
            >
              Abbrechen
            </BtnPrimary>
            <BtnPrimary onClick={handleExtendCampaign} disabled={isExtendingCampaign}>
              Ja, verlängern
            </BtnPrimary>
          </ActionButtonWrapper>
        </>
      )}
    </ModalContentWrapper>
  );
};

const ExtensionButton = ({
  campaignName,
  remainingDays,
  campaignId,
  locations,
  channels,
  lifetimeBudget,
  campaignDuration,
  metrics,
  isLeadCampaign
}) => {
  const { newLifetimeBudget, newMediaBudget } = useMemo(
    () =>
      calculateBudget({
        lifetimeBudget,
        duration: campaignDuration,
        newDuration: DEFAULT_EXTENSION_DURATION
      }),
    [lifetimeBudget, campaignDuration]
  );

  const onClickButton = useCallback(() => {
    Actions.toggleModal("showGenericModal", true, {
      headline: "Kampagne verlängern",
      widthVersion: "medium",
      view: (
        <ModalContent
          remainingDays={remainingDays}
          channels={channels}
          price={newLifetimeBudget}
          isLeadCampaign={isLeadCampaign}
          campaignId={campaignId}
          newLifetimeBudget={newLifetimeBudget}
          newMediaBudget={newMediaBudget}
          locations={locations}
          campaignName={campaignName}
        />
      )
    });
  }, [
    remainingDays,
    newLifetimeBudget,
    channels,
    isLeadCampaign,
    newMediaBudget,
    locations,
    campaignName,
    campaignId
  ]);

  return (
    <MainWrapper>
      <Button onClick={onClickButton}> 🔄 14 Tage verlängern</Button>
    </MainWrapper>
  );
};

export default ExtensionButton;
